import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Shield, Plus, Loader2, RefreshCw, Copy, ChevronRight, X, TrendingUp,
  CheckCircle2, XCircle, Trash2, ExternalLink, ArrowUpRight, Ban,
} from "lucide-react";

/**
 * DepositsTab — Gestion des cautions Stripe (holds manual capture).
 *
 * Flow admin :
 *   1. Créer une caution → génère un lien Checkout Stripe partagé
 *   2. Client clique le lien, saisit sa carte sur Stripe (PCI-compliant)
 *   3. Backend passe le statut à `requires_capture` (fonds bloqués)
 *   4. Admin peut : incrémenter le hold, capturer tout/partie, annuler
 *
 * Aucun secret Stripe n'est jamais exposé côté client — toutes les
 * opérations passent par le backend qui détient la clé secrète.
 */

// Métadonnées des événements de la timeline — labels français,
// couleurs et emojis pour une lecture claire côté admin.
const EVENT_META = {
  created: { label: "Créée", color: "bg-neutral-500", emoji: "🆕" },
  paid: { label: "Autorisée", color: "bg-blue-500", emoji: "🔒" },
  incremented: { label: "Augmentée", color: "bg-emerald-500", emoji: "⬆️" },
  increment_failed: { label: "Augmentation refusée", color: "bg-red-500", emoji: "🚫" },
  increment_unavailable: { label: "Augmentation impossible", color: "bg-amber-500", emoji: "⚠️" },
  captured: { label: "Capturée", color: "bg-primary", emoji: "💰" },
  partial_capture: { label: "Capture partielle", color: "bg-primary", emoji: "💰" },
  cancelled: { label: "Annulée", color: "bg-neutral-700", emoji: "↩️" },
  canceled: { label: "Annulée", color: "bg-neutral-700", emoji: "↩️" },
  expired: { label: "Expirée", color: "bg-amber-600", emoji: "⏱️" },
  rental_end_updated: { label: "Fin de location mise à jour", color: "bg-neutral-500", emoji: "📅" },
};

// ---- Utilitaires de couverture -------------------------------------------
// Aucune valeur théorique. Toutes les dates viennent de Stripe (capture_before)
// ou de la saisie admin (rental_end_at). Les calculs sont faits côté client
// pour un rafraîchissement live du "temps restant".
function parseIso(iso) {
  if (!iso) return null;
  try { const d = new Date(iso); return isNaN(d.getTime()) ? null : d; } catch { return null; }
}
function formatFR(date) {
  if (!date) return "—";
  return date.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function diffHumanFR(fromDate, toDate) {
  // Retourne "26 j 4 h", ou "0 j 3 h" — pas de "année" (auth Stripe < 30 j).
  if (!fromDate || !toDate) return "—";
  let ms = toDate.getTime() - fromDate.getTime();
  if (ms <= 0) return "expirée";
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  ms -= days * 24 * 60 * 60 * 1000;
  const hours = Math.floor(ms / (1000 * 60 * 60));
  return `${days} j ${hours} h`;
}

export default function DepositsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/price/deposits?limit=100");
      setItems(data?.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const activeCount = useMemo(
    () => items.filter((d) => d.status === "requires_capture" || d.status === "requires_action").length,
    [items],
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-2xl bg-white p-4 street-border street-shadow">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div>
            <div className="flex items-center gap-2">
              <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-primary-foreground street-border-thin">
                <Shield className="h-4 w-4" />
              </div>
              <h2 className="font-[Outfit] text-[18px] font-black leading-tight">Cautions Stripe</h2>
            </div>
            <p className="mt-1 text-[12px] text-foreground/60 font-medium">
              Bloquer une empreinte carte sans la débiter — pour locations, dépôts de garantie, événements.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={load}
              disabled={loading}
              data-testid="deposits-refresh-btn"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white street-border-thin street-press disabled:opacity-50"
              title="Rafraîchir"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </button>
            <button
              onClick={() => setCreateOpen(true)}
              data-testid="deposits-new-btn"
              className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-[12px] font-black text-primary-foreground street-border-thin street-press"
            >
              <Plus className="h-4 w-4" /> Nouvelle caution
            </button>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <StatCard label="Total" value={items.length} />
          <StatCard label="Actives" value={activeCount} tone="primary" />
          <StatCard
            label="Capturé (€)"
            value={items
              .reduce((s, d) => s + (d.amount_captured_eur || 0), 0)
              .toFixed(2)}
          />
        </div>
      </div>

      {/* List */}
      {loading && !items.length ? (
        <div className="rounded-2xl bg-white p-8 street-border street-shadow grid place-items-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-white p-8 street-border street-shadow text-center">
          <div className="text-[14px] font-bold text-foreground/60">Aucune caution pour le moment.</div>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="deposits-empty-cta"
            className="mt-3 inline-flex items-center gap-1 rounded-lg bg-primary px-4 py-2 text-[13px] font-black text-primary-foreground street-border-thin street-press"
          >
            <Plus className="h-4 w-4" /> Créer la première
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((d) => (
            <DepositRow
              key={d.id}
              d={d}
              onOpen={() => setDetailId(d.id)}
              testid={`deposit-row-${d.id}`}
            />
          ))}
        </div>
      )}

      {createOpen && (
        <DepositCreateModal
          onClose={() => setCreateOpen(false)}
          onCreated={(newDep) => { setCreateOpen(false); load(); setDetailId(newDep.id); }}
        />
      )}
      {detailId && (
        <DepositDetailModal
          depositId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  return (
    <div className={`rounded-xl street-border-thin p-3 ${tone === "primary" ? "bg-primary/10" : "bg-neutral-50"}`}>
      <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60">{label}</div>
      <div className="mt-1 font-mono text-[18px] font-black leading-none">{value}</div>
    </div>
  );
}

function statusBadge(status) {
  const map = {
    created: { bg: "bg-neutral-200", fg: "text-neutral-900", label: "Créée" },
    requires_payment_method: { bg: "bg-yellow-200", fg: "text-yellow-900", label: "En attente paiement" },
    requires_action: { bg: "bg-orange-200", fg: "text-orange-900", label: "Action requise" },
    requires_capture: { bg: "bg-emerald-200", fg: "text-emerald-900", label: "Bloquée ✓" },
    succeeded: { bg: "bg-blue-200", fg: "text-blue-900", label: "Capturée" },
    canceled: { bg: "bg-red-200", fg: "text-red-900", label: "Annulée" },
    expired: { bg: "bg-neutral-300", fg: "text-neutral-800", label: "Expirée" },
  };
  return map[status] || { bg: "bg-neutral-200", fg: "text-neutral-900", label: status || "—" };
}

function DepositRow({ d, onOpen, testid }) {
  const badge = statusBadge(d.status);
  // Point de couverture (vert/orange/rouge) — visible d'un coup d'œil
  // même sans ouvrir le détail. Se base UNIQUEMENT sur les dates
  // Stripe (capture_before) vs la fin de location saisie.
  let dot = null;
  if (d.capture_before_at && d.rental_end_at) {
    const cb = new Date(d.capture_before_at).getTime();
    const re = new Date(d.rental_end_at).getTime();
    if (!isNaN(cb) && !isNaN(re)) {
      const diffDays = (cb - re) / (1000 * 60 * 60 * 24);
      if (diffDays < 0) dot = { color: "bg-red-500", title: "Caution expire AVANT la restitution" };
      else if (diffDays < 3) dot = { color: "bg-amber-500", title: `Marge de ${diffDays.toFixed(1)} j — attention` };
      else dot = { color: "bg-emerald-500", title: `Couvre bien (marge ${diffDays.toFixed(0)} j)` };
    }
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={testid}
      className="w-full text-left rounded-2xl bg-white p-3 street-border-thin street-shadow-sm street-press flex items-center gap-3"
    >
      {dot && (
        <span
          className={`h-2.5 w-2.5 flex-none rounded-full ${dot.color}`}
          title={dot.title}
          data-testid={`${testid}-coverage-dot`}
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-[Outfit] text-[13px] font-black truncate">{d.rental_ref}</span>
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest street-border-thin ${badge.bg} ${badge.fg}`}>
            {badge.label}
          </span>
        </div>
        <div className="mt-0.5 text-[11px] text-foreground/60 font-medium truncate">
          {d.client_email || d.client_name || "—"} · créée le {(d.created_at || "").slice(0, 10)}
        </div>
      </div>
      <div className="text-right">
        <div className="font-mono text-[15px] font-black leading-none">
          {(d.amount_authorized_eur ?? d.amount_initial_eur ?? 0).toFixed(2)}€
        </div>
        {d.amount_captured_eur > 0 && (
          <div className="mt-0.5 text-[10px] text-blue-700 font-bold">
            capturé: {d.amount_captured_eur.toFixed(2)}€
          </div>
        )}
      </div>
      <ChevronRight className="h-4 w-4 text-foreground/40 flex-none" />
    </button>
  );
}

function DepositCreateModal({ onClose, onCreated }) {
  const [amount, setAmount] = useState("800");
  const [ref, setRef] = useState("");
  const [productName, setProductName] = useState("");
  const [description, setDescription] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientName, setClientName] = useState("");
  const [notes, setNotes] = useState("");
  const [rentalEnd, setRentalEnd] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy) return;
    const amt = Number(amount);
    if (!amt || amt < 1) return toast.error("Montant invalide (min 1€)");
    if (!ref.trim()) return toast.error("Référence obligatoire (numéro de location, contrat…)");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/price/deposits", {
        amount_eur: amt,
        rental_ref: ref.trim(),
        product_name: productName.trim() || undefined,
        description: description.trim() || undefined,
        client_email: clientEmail.trim() || undefined,
        client_name: clientName.trim() || undefined,
        notes: notes.trim() || undefined,
        rental_end_at: rentalEnd ? new Date(rentalEnd).toISOString() : undefined,
      });
      toast.success("✅ Caution créée — copie le lien Stripe et envoie-le au client");
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Nouvelle caution Stripe" onClose={onClose} testid="deposit-create-modal">
      <div className="space-y-3">
        <FieldRow label="Montant à bloquer (€)">
          <input
            type="number"
            step="1"
            min="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="input"
            data-testid="deposit-amount-input"
          />
        </FieldRow>
        <FieldRow label="Référence de location / contrat">
          <input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            className="input"
            placeholder="Ex: LOC-2026-042"
            data-testid="deposit-ref-input"
          />
        </FieldRow>
        <FieldRow label="Nom affiché sur la page Stripe (optionnel)">
          <input
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            className="input"
            placeholder={`Défaut : "Caution — ${ref.trim() || "REF"}"`}
            maxLength={200}
            data-testid="deposit-product-name-input"
          />
          <div className="mt-1 text-[10px] text-foreground/60">
            Ce que le client verra en haut de la page de paiement Stripe.
          </div>
        </FieldRow>
        <FieldRow label="Description (visible sur Stripe)">
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="input"
            placeholder='Défaut : "Empreinte carte remboursable (caution non prélevée)"'
            maxLength={500}
            data-testid="deposit-desc-input"
          />
        </FieldRow>
        <div className="grid grid-cols-2 gap-3">
          <FieldRow label="Email client (pré-remplit Checkout)">
            <input
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              className="input"
              placeholder="nadir@…"
              data-testid="deposit-email-input"
            />
          </FieldRow>
          <FieldRow label="Nom (optionnel)">
            <input
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className="input"
              placeholder="M. Nadir"
              data-testid="deposit-name-input"
            />
          </FieldRow>
        </div>
        <FieldRow label="Notes internes (audit)">
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="input resize-none"
            placeholder="Ex: réservation via téléphone, retrait vendredi"
          />
        </FieldRow>
        <FieldRow label="Date/heure de fin de location prévue (optionnel)">
          <input
            type="datetime-local"
            value={rentalEnd}
            onChange={(e) => setRentalEnd(e.target.value)}
            className="input"
            data-testid="deposit-rental-end-input"
          />
          <div className="mt-1 text-[10px] text-foreground/60">
            Utilisée pour comparer avec l'expiration réelle de la préautorisation Stripe (couvre / ne couvre pas).
          </div>
        </FieldRow>
        <div className="rounded-xl bg-yellow-50 street-border-thin p-3 text-[12px] text-yellow-900">
          🔒 <b>Sécurité</b> — La carte sera saisie par le client sur la page Stripe (PCI-compliant).
          Aucune donnée bancaire ne transite par Price. La caution est <b>bloquée</b>, pas prélevée :
          tu la libères ou la captures depuis cet écran.
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button
          onClick={onClose}
          disabled={busy}
          data-testid="deposit-create-cancel"
          className="inline-flex items-center rounded-lg bg-neutral-200 px-3 py-2 text-[12px] font-black text-neutral-900 street-border-thin street-press disabled:opacity-50"
        >
          Annuler
        </button>
        <button
          onClick={submit}
          disabled={busy}
          data-testid="deposit-create-submit"
          className="inline-flex items-center gap-1 rounded-lg bg-primary px-4 py-2 text-[13px] font-black text-primary-foreground street-border-thin street-press disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Créer la caution
        </button>
      </div>
    </ModalShell>
  );
}

function DepositDetailModal({ depositId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [incAmount, setIncAmount] = useState("");
  const [captureAmount, setCaptureAmount] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/price/deposits/${depositId}`);
      setD(data);
    } catch (err) {
      toast.error(formatApiError(err));
      onClose();
    }
  };
  useEffect(() => { load(); }, [depositId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (verb) => {
    if (busy) return;
    setBusy(verb);
    try {
      let path = `/admin/price/deposits/${depositId}/${verb}`;
      let body = {};
      if (verb === "increment") {
        const v = Number(incAmount);
        if (!v || v < 1) { toast.error("Montant invalide"); setBusy(""); return; }
        body = { amount_to_add_eur: v };
      } else if (verb === "capture") {
        const v = Number(captureAmount);
        if (captureAmount && (isNaN(v) || v <= 0)) { toast.error("Montant invalide"); setBusy(""); return; }
        body = captureAmount ? { amount_eur: v } : {};
      }
      const method = verb === "delete" ? "delete" : "post";
      const url = verb === "delete" ? `/admin/price/deposits/${depositId}` : path;
      const res = method === "post" ? await api.post(url, body) : await api.delete(url);
      toast.success(
        verb === "increment" ? "✅ Autorisation augmentée" :
        verb === "capture" ? "✅ Caution capturée" :
        verb === "cancel" ? "✅ Caution annulée (fonds libérés)" :
        "✅ Caution supprimée"
      );
      onChanged && onChanged();
      if (verb === "delete") onClose();
      else setD(res.data);
      setIncAmount(""); setCaptureAmount("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const copyLink = async () => {
    const url = `https://pricecovoit.fr/deposit/${d.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("📋 Lien copié — envoie-le au client");
    } catch {
      toast.error("Copie impossible — sélectionne l'URL à la main");
    }
  };

  if (!d) {
    return (
      <ModalShell title="Chargement…" onClose={onClose}>
        <div className="grid place-items-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      </ModalShell>
    );
  }

  const badge = statusBadge(d.status);
  const canIncrement = d.status === "requires_capture";
  const canCapture = d.status === "requires_capture";
  const canCancel = ["created", "requires_payment_method", "requires_action", "requires_capture"].includes(d.status);
  const canDelete = ["canceled", "succeeded", "expired"].includes(d.status);
  const shareUrl = `https://pricecovoit.fr/deposit/${d.share_token}`;

  return (
    <ModalShell
      title={`Caution ${d.rental_ref}`}
      onClose={onClose}
      testid="deposit-detail-modal"
      right={
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest street-border-thin ${badge.bg} ${badge.fg}`}>
          {badge.label}
        </span>
      }
    >
      <div className="space-y-4">
        {/* Amount summary */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-emerald-50 street-border-thin p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Autorisé</div>
            <div className="mt-1 font-mono text-[22px] font-black leading-none">
              {(d.amount_authorized_eur ?? 0).toFixed(2)}€
            </div>
          </div>
          <div className="rounded-xl bg-blue-50 street-border-thin p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Capturé</div>
            <div className="mt-1 font-mono text-[22px] font-black leading-none">
              {(d.amount_captured_eur ?? 0).toFixed(2)}€
            </div>
          </div>
        </div>

        {/* Bloc Expiration & Couverture — pilotée par capture_before Stripe (jamais une valeur théorique). */}
        <DepositCoverageBlock d={d} onRentalEndSaved={(fresh) => { setD(fresh); onChanged && onChanged(); }} />

        {/* Share link */}
        {d.status !== "canceled" && d.status !== "succeeded" && (
          <div className="rounded-xl bg-yellow-50 street-border-thin p-3">
            <div className="text-[11px] font-black uppercase tracking-widest text-yellow-900">🔗 Lien à envoyer au client</div>
            <div className="mt-2 flex items-center gap-2">
              <input
                readOnly
                value={shareUrl}
                className="input flex-1 !text-[11px] font-mono"
                data-testid="deposit-share-url"
                onClick={(e) => e.target.select()}
              />
              <button
                type="button"
                onClick={copyLink}
                data-testid="deposit-copy-link-btn"
                className="rounded-lg bg-neutral-900 px-3 py-2 text-white street-border-thin street-press"
                title="Copier"
              >
                <Copy className="h-4 w-4" />
              </button>
              <a
                href={d.checkout_url}
                target="_blank"
                rel="noreferrer"
                data-testid="deposit-open-checkout-btn"
                className="rounded-lg bg-primary px-3 py-2 text-primary-foreground street-border-thin street-press"
                title="Ouvrir Checkout"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        )}

        {/* Client info */}
        <div className="rounded-xl bg-neutral-50 street-border-thin p-3 space-y-1 text-[12px]">
          <InfoLine label="Client">{d.client_name || "—"} · {d.client_email || "—"}</InfoLine>
          <InfoLine label="Description">{d.description || "—"}</InfoLine>
          <InfoLine label="Notes">{d.notes || "—"}</InfoLine>
          <InfoLine label="PaymentIntent">
            <span className="font-mono text-[10px] break-all">{d.payment_intent_id || "—"}</span>
          </InfoLine>
          <InfoLine label="Créée par">{d.created_by_email || "—"} · {(d.created_at || "").slice(0, 16).replace("T", " ")}</InfoLine>
        </div>

        {/* Actions */}
        {canIncrement && (
          <div className="rounded-xl bg-white street-border-thin p-3 space-y-2">
            <div className="text-[11px] font-black uppercase tracking-widest text-foreground/60">
              <TrendingUp className="inline h-3 w-3 mr-1" /> Augmenter l'autorisation
            </div>
            <div className="flex gap-2">
              <input
                type="number"
                step="10"
                value={incAmount}
                onChange={(e) => setIncAmount(e.target.value)}
                className="input flex-1"
                placeholder="+ montant en €"
                data-testid="deposit-increment-input"
              />
              <button
                onClick={() => act("increment")}
                disabled={busy === "increment"}
                data-testid="deposit-increment-btn"
                className="rounded-lg bg-emerald-500 px-4 py-2 text-[12px] font-black text-white street-border-thin street-press disabled:opacity-50"
              >
                {busy === "increment" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
              </button>
            </div>
            <div className="text-[11px] text-foreground/60">
              La banque peut refuser (carte non éligible). Nouveau total = actuel + montant saisi.
            </div>
          </div>
        )}

        {canCapture && (
          <div className="rounded-xl bg-white street-border-thin p-3 space-y-2">
            <div className="text-[11px] font-black uppercase tracking-widest text-foreground/60">
              <CheckCircle2 className="inline h-3 w-3 mr-1" /> Capturer (débit effectif)
            </div>
            <div className="flex gap-2">
              <input
                type="number"
                step="1"
                value={captureAmount}
                onChange={(e) => setCaptureAmount(e.target.value)}
                className="input flex-1"
                placeholder={`Laisser vide = tout capturer (${(d.amount_authorized_eur ?? 0).toFixed(2)}€)`}
                data-testid="deposit-capture-input"
              />
              <button
                onClick={() => act("capture")}
                disabled={busy === "capture"}
                data-testid="deposit-capture-btn"
                className="rounded-lg bg-blue-500 px-4 py-2 text-[12px] font-black text-white street-border-thin street-press disabled:opacity-50"
              >
                {busy === "capture" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {canCancel && (
            <button
              onClick={() => act("cancel")}
              disabled={busy === "cancel"}
              data-testid="deposit-cancel-btn"
              className="inline-flex items-center gap-1 rounded-lg bg-red-500 px-3 py-2 text-[12px] font-black text-white street-border-thin street-press disabled:opacity-50"
            >
              {busy === "cancel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
              Annuler (libérer les fonds)
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => act("delete")}
              disabled={busy === "delete"}
              data-testid="deposit-delete-btn"
              className="inline-flex items-center gap-1 rounded-lg bg-neutral-900 px-3 py-2 text-[12px] font-black text-white street-border-thin street-press disabled:opacity-50"
            >
              {busy === "delete" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Retirer de la liste
            </button>
          )}
        </div>

        {/* Events timeline */}
        <div>
          <div className="text-[11px] font-black uppercase tracking-widest text-foreground/60 mb-2">
            Historique
          </div>
          <div className="space-y-1">
            {(!d.events || d.events.length === 0) && (
              <div className="rounded-lg bg-neutral-50 street-border-thin p-3 text-center text-[11px] text-foreground/50">
                Aucun événement pour l'instant
              </div>
            )}
            {(d.events || []).slice().reverse().map((e, i) => {
              const meta = EVENT_META[e.type] || { label: e.type, color: "bg-neutral-400", emoji: "•" };
              const isError = e.error_message || e.error_code;
              return (
                <div
                  key={i}
                  className={`rounded-lg street-border-thin p-2 text-[11px] ${isError ? "bg-red-50" : "bg-neutral-50"}`}
                  data-testid={`deposit-event-${e.type}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-full ${meta.color} px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white`}>
                      <span>{meta.emoji}</span> {meta.label}
                    </span>
                    <span className="text-foreground/50 font-mono text-[10px]">{(e.at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="mt-1 text-foreground/70 leading-snug">
                    {e.admin_email && <span className="font-medium">{e.admin_email}</span>}
                    {e.amount_eur != null && <span> · {Number(e.amount_eur).toFixed(2)}€</span>}
                    {e.amount_to_add_eur != null && <span> · +{Number(e.amount_to_add_eur).toFixed(2)}€</span>}
                    {e.new_amount_eur != null && <span> → total {Number(e.new_amount_eur).toFixed(2)}€</span>}
                    {isError && (
                      <div className="mt-1 text-red-700 font-medium">
                        ⚠️ {e.error_message || e.error_code}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

function DepositCoverageBlock({ d, onRentalEndSaved }) {
  const captureBefore = parseIso(d.capture_before_at);
  const rentalEnd = parseIso(d.rental_end_at);
  const [editing, setEditing] = useState(false);
  const [localEnd, setLocalEnd] = useState(
    d.rental_end_at ? new Date(d.rental_end_at).toISOString().slice(0, 16) : ""
  );
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(new Date());

  // Countdown live — tick chaque minute pour rafraîchir "temps restant".
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const incLabel = d.incremental_authorization_status === "available" ? "Disponible" :
    (d.incremental_authorization_status === "unavailable" ? "Non disponible" : "—");
  const extLabel = d.extended_authorization_status === "enabled" ? "Disponible" :
    (d.extended_authorization_status === "disabled" ? "Non disponible" : "—");
  const incColor = d.incremental_authorization_status === "available" ? "bg-emerald-100 text-emerald-900" :
    (d.incremental_authorization_status === "unavailable" ? "bg-red-100 text-red-900" : "bg-neutral-100 text-neutral-700");
  const extColor = d.extended_authorization_status === "enabled" ? "bg-emerald-100 text-emerald-900" :
    (d.extended_authorization_status === "disabled" ? "bg-red-100 text-red-900" : "bg-neutral-100 text-neutral-700");

  // Statut de couverture (vert/orange/rouge) — pilotée par la vraie
  // date Stripe capture_before comparée à la date de fin de location.
  let coverage = null;
  if (captureBefore && rentalEnd) {
    const bufferMs = captureBefore.getTime() - rentalEnd.getTime();
    const bufferDays = bufferMs / (1000 * 60 * 60 * 24);
    if (bufferMs < 0) coverage = {
      tone: "red",
      label: "❌ Ne couvre pas — expiration avant restitution",
      detail: `La préautorisation bancaire expire avant la nouvelle date de restitution. Cette caution ne peut pas couvrir toute la prolongation.`,
    };
    else if (bufferDays < 3) coverage = {
      tone: "orange",
      label: `⚠️ Couverture limite (${bufferDays.toFixed(1)} j de marge)`,
      detail: `La caution expire moins de 3 jours après la fin de location prévue. Prévois de capturer avant expiration.`,
    };
    else coverage = {
      tone: "green",
      label: `✅ Couvre toute la location (marge ${bufferDays.toFixed(0)} j)`,
      detail: null,
    };
  }

  const saveRentalEnd = async () => {
    setSaving(true);
    try {
      const iso = localEnd ? new Date(localEnd).toISOString() : null;
      const { data } = await api.patch(
        `/admin/price/deposits/${d.id}/rental-end`,
        { rental_end_at: iso },
      );
      toast.success("✅ Date de fin mise à jour");
      setEditing(false);
      onRentalEndSaved && onRentalEndSaved(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const coverageBg = coverage?.tone === "green" ? "bg-emerald-50 border-emerald-500" :
    coverage?.tone === "orange" ? "bg-amber-50 border-amber-500" :
    coverage?.tone === "red" ? "bg-red-50 border-red-500" : "bg-neutral-50 border-neutral-300";

  return (
    <div className="rounded-xl bg-white street-border-thin p-3 space-y-3" data-testid="deposit-coverage-block">
      <div className="text-[11px] font-black uppercase tracking-widest text-foreground/60">
        Préautorisation & Couverture
      </div>

      {/* Ligne : expiration Stripe */}
      {captureBefore ? (
        <div className="rounded-lg bg-neutral-50 street-border-thin p-2.5 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-black uppercase tracking-wider text-foreground/60">
              Expiration Stripe (capture_before)
            </span>
            <span className="font-mono text-[12px] font-black" data-testid="deposit-capture-before">
              {formatFR(captureBefore)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-medium text-foreground/60">Temps restant</span>
            <span className="font-mono text-[12px] font-black">{diffHumanFR(now, captureBefore)}</span>
          </div>
        </div>
      ) : (
        <div className="rounded-lg bg-neutral-50 street-border-thin p-2.5 text-[11px] text-foreground/60">
          Expiration inconnue — le client n'a pas encore payé ou Stripe n'a pas encore renvoyé <code>capture_before</code>.
        </div>
      )}

      {/* Autorisations */}
      <div className="grid grid-cols-2 gap-2">
        <div className={`rounded-lg street-border-thin p-2 ${incColor}`}>
          <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Autor. incrémentale</div>
          <div className="mt-0.5 text-[13px] font-black" data-testid="deposit-inc-status">{incLabel}</div>
        </div>
        <div className={`rounded-lg street-border-thin p-2 ${extColor}`}>
          <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Autor. prolongée</div>
          <div className="mt-0.5 text-[13px] font-black" data-testid="deposit-ext-status">{extLabel}</div>
        </div>
      </div>

      {/* Plafond incrémentation Stripe — RÈGLE PUBLIQUE documentée par
          Stripe. On calcule uniquement le plafond par tentative
          (max(500€, 500% du montant autorisé)). Aucune valeur "garantie"
          n'est présentée — l'acceptation banque/carte reste requise. */}
      {d.incremental_authorization_status === "available" && d.amount_authorized_eur > 0 && (() => {
        const authorized = Number(d.amount_authorized_eur || 0);
        // Règle Stripe : chaque incrément est plafonné à max(500€, 500% × autorisé actuel).
        const perAttemptCapEur = Math.max(500, authorized * 5);
        const maxTotalPerAttemptEur = authorized + perAttemptCapEur;
        return (
          <div
            className="rounded-lg bg-indigo-50 street-border-thin p-2.5 text-[11px]"
            data-testid="deposit-inc-ceiling"
          >
            <div className="text-[10px] font-black uppercase tracking-widest text-indigo-900">
              Plafond d'augmentation (règle Stripe)
            </div>
            <div className="mt-1.5 space-y-0.5">
              <div className="flex items-center justify-between">
                <span className="text-foreground/70">Autorisé actuellement</span>
                <span className="font-mono font-black">{authorized.toFixed(2)}€</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-foreground/70">Augmentation max par tentative</span>
                <span className="font-mono font-black text-indigo-900" data-testid="deposit-inc-cap">
                  +{perAttemptCapEur.toFixed(2)}€
                </span>
              </div>
              <div className="flex items-center justify-between border-t border-indigo-200 pt-1 mt-1">
                <span className="font-black text-indigo-900">Total max après un incrément</span>
                <span className="font-mono font-black text-indigo-900" data-testid="deposit-inc-max-total">
                  {maxTotalPerAttemptEur.toFixed(2)}€
                </span>
              </div>
            </div>
            <div className="mt-2 text-[10px] text-foreground/60 leading-snug">
              🏦 <b>Règle Stripe</b> : chaque incrément est plafonné à <b>max(500 €, 500 % du montant autorisé actuel)</b>.
              10 tentatives max par caution. Cette augmentation reste soumise à l'acceptation de la banque et du réseau de carte (Visa/MC/Amex/Discover).
            </div>
          </div>
        );
      })()}

      {/* Rental end — édition */}
      <div className="rounded-lg bg-neutral-50 street-border-thin p-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-black uppercase tracking-wider text-foreground/60">
            Fin de location prévue
          </span>
          {!editing && (
            <button
              type="button"
              onClick={() => { setLocalEnd(d.rental_end_at ? new Date(d.rental_end_at).toISOString().slice(0, 16) : ""); setEditing(true); }}
              className="text-[10px] font-black uppercase tracking-wider text-primary underline underline-offset-2"
              data-testid="deposit-rental-end-edit"
            >
              Modifier
            </button>
          )}
        </div>
        {editing ? (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="datetime-local"
              value={localEnd}
              onChange={(e) => setLocalEnd(e.target.value)}
              className="input flex-1"
              data-testid="deposit-rental-end-input-edit"
            />
            <button
              type="button"
              onClick={saveRentalEnd}
              disabled={saving}
              className="rounded-lg bg-primary px-3 py-1.5 text-[11px] font-black text-primary-foreground street-border-thin street-press disabled:opacity-50"
              data-testid="deposit-rental-end-save"
            >
              {saving ? "…" : "OK"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-lg bg-neutral-200 px-3 py-1.5 text-[11px] font-black street-border-thin street-press"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="mt-1 font-mono text-[12px] font-black">
            {rentalEnd ? formatFR(rentalEnd) : <span className="text-foreground/50">— non renseignée</span>}
          </div>
        )}
      </div>

      {/* Bannière couverture — pilotée capture_before vs rental_end */}
      {coverage && (
        <div
          className={`rounded-xl border-2 p-3 ${coverageBg}`}
          data-testid={`deposit-coverage-${coverage.tone}`}
        >
          <div className="font-[Outfit] text-[14px] font-black">
            {coverage.label}
          </div>
          {coverage.detail && (
            <div className="mt-1 text-[11px] font-medium opacity-80">{coverage.detail}</div>
          )}
          {captureBefore && (
            <div className="mt-2 text-[11px] text-foreground/70">
              🛡️ Cette caution permet de couvrir la location jusqu'au <b>{formatFR(captureBefore)}</b>.
            </div>
          )}
        </div>
      )}

      {/* Prolongation possible — indicateur explicite pour l'admin */}
      {captureBefore && !editing && (
        <div className="rounded-lg bg-blue-50 street-border-thin p-2.5 text-[11px]">
          <b>Prolongation possible avec cette caution :</b>{" "}
          <span data-testid="deposit-extension-info">
            jusqu'au <b>{formatFR(captureBefore)}</b>
            {rentalEnd && (() => {
              const extraDays = Math.floor((captureBefore.getTime() - rentalEnd.getTime()) / (1000 * 60 * 60 * 24));
              if (extraDays > 0) return ` (${extraDays} jour${extraDays > 1 ? "s" : ""} supplémentaire${extraDays > 1 ? "s" : ""} au max)`;
              if (extraDays < 0) return ` — la fin de location est APRÈS l'expiration Stripe`;
              return ` (fin de location = expiration)`;
            })()}
          </span>
        </div>
      )}
    </div>
  );
}



function ModalShell({ title, onClose, children, right, testid }) {
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      data-testid={testid}
    >
      <div className="w-full sm:max-w-2xl max-h-[95vh] overflow-hidden rounded-t-3xl sm:rounded-3xl bg-white street-border street-shadow flex flex-col">
        <div className="flex items-center justify-between gap-2 px-4 py-3 bg-primary/10 border-b-4 border-foreground">
          <div className="min-w-0 flex items-center gap-2">
            <div className="font-[Outfit] text-[15px] font-black truncate">{title}</div>
            {right}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="modal-close-btn"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white street-border-thin street-press"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
      </div>
      <style>{`
        .input {
          width: 100%;
          border: 3px solid #1E0910;
          background: #FFFFFF;
          padding: 8px 12px;
          border-radius: 12px;
          font-family: 'Outfit', sans-serif;
          font-size: 14px;
          font-weight: 600;
          box-shadow: 3px 3px 0 0 #1E0910;
        }
        .input:focus { outline: none; box-shadow: 4px 4px 0 0 #F86A78; }
      `}</style>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-black uppercase tracking-wider text-foreground/60">{label}</div>
      {children}
    </label>
  );
}

function InfoLine({ label, children }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-[10px] font-black uppercase tracking-wider text-foreground/60 w-24 flex-none pt-0.5">{label}</span>
      <span className="text-foreground/90 min-w-0 break-words">{children}</span>
    </div>
  );
}
